import React, { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const TeamManagement = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [formData, setFormData] = useState({ name: '', role: '', bio: '', image_url: '', display_order: 0 });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase?.from('team_members')?.select('*')?.order('display_order', { ascending: true });

      if (fetchError) throw fetchError;
      setTeamMembers(data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching team members:', err);
      setError(err?.message || 'Failed to fetch team members');
    } finally {
      setLoading(false);
    }
  };

  const onDrop = async (acceptedFiles) => {
    const file = acceptedFiles?.[0];
    if (!file) return;

    try {
      setUploading(true);
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${Date.now()}-${Math.random()?.toString(36)?.substring(7)}.${fileExt}`;

      const { error: uploadError } = await supabase?.storage?.from('team-photos')?.upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase?.storage?.from('team-photos')?.getPublicUrl(fileName);

      setFormData({ ...formData, image_url: publicUrl });
    } catch (err) {
      console.error('Error uploading image:', err);
      alert('Failed to upload image: ' + err?.message);
    } finally {
      setUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.webp'] },
    maxFiles: 1
  });

  const handleEdit = (member) => {
    setEditingMember(member);
    setFormData({
      name: member?.name || '',
      role: member?.role || '',
      bio: member?.bio || '',
      image_url: member?.image_url || '',
      display_order: member?.display_order || 0
    });
    setShowForm(true);
  };

  const handleCreate = () => {
    setEditingMember(null);
    setFormData({ name: '', role: '', bio: '', image_url: '', display_order: teamMembers?.length || 0 });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formData?.name || !formData?.role) {
      alert('Please fill in name and role');
      return;
    }

    try {
      setSaving(true);
      if (editingMember?.id) {
        const { error: updateError } = await supabase?.from('team_members')?.update(formData)?.eq('id', editingMember?.id);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase?.from('team_members')?.insert([formData]);

        if (insertError) throw insertError;
      }

      setShowForm(false);
      setEditingMember(null);
      setFormData({ name: '', role: '', bio: '', image_url: '', display_order: 0 });
      await fetchTeamMembers();
    } catch (err) {
      console.error('Error saving team member:', err);
      alert('Failed to save team member: ' + err?.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (memberId) => {
    if (!confirm('Are you sure you want to delete this team member?')) return;

    try {
      const { error: deleteError } = await supabase?.from('team_members')?.delete()?.eq('id', memberId);

      if (deleteError) throw deleteError;
      await fetchTeamMembers();
    } catch (err) {
      console.error('Error deleting team member:', err);
      alert('Failed to delete team member: ' + err?.message);
    }
  };

  const handleToggleActive = async (memberId, currentStatus) => {
    try {
      const { error: updateError } = await supabase?.from('team_members')?.update({ is_active: !currentStatus })?.eq('id', memberId);

      if (updateError) throw updateError;
      await fetchTeamMembers();
    } catch (err) {
      console.error('Error toggling status:', err);
      alert('Failed to update status: ' + err?.message);
    }
  };

  if (showForm) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground">
              {editingMember ? 'Edit Team Member' : 'Add Team Member'}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {editingMember ? 'Update team member information' : 'Add a new team member'}
            </p>
          </div>
          <Button variant="outline" onClick={() => setShowForm(false)} iconName="X">
            Close
          </Button>
        </div>

        <div className="bg-card border border-border rounded-lg p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Name"
              required
              value={formData?.name}
              onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
              placeholder="John Doe"
            />
            <Input
              label="Role"
              required
              value={formData?.role}
              onChange={(e) => setFormData({ ...formData, role: e?.target?.value })}
              placeholder="Chief Fire Investigation Officer"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Bio
            </label>
            <textarea
              value={formData?.bio}
              onChange={(e) => setFormData({ ...formData, bio: e?.target?.value })}
              placeholder="Brief bio about the team member"
              rows={3}
              className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            />
          </div>

          <Input
            label="Display Order"
            type="number"
            value={formData?.display_order}
            onChange={(e) => setFormData({ ...formData, display_order: parseInt(e?.target?.value) || 0 })}
            placeholder="0"
          />

          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Photo
            </label>
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-smooth ${
                isDragActive
                  ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
              }`}
            >
              <input {...getInputProps()} />
              {uploading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
                  <span className="text-sm text-muted-foreground">Uploading...</span>
                </div>
              ) : formData?.image_url ? (
                <div className="space-y-2">
                  <img
                    src={formData?.image_url}
                    alt="Team member"
                    className="max-h-40 mx-auto rounded-lg"
                  />
                  <p className="text-sm text-muted-foreground">
                    Click or drag to replace image
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Icon name="Upload" size={32} className="mx-auto text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    Drag and drop a photo, or click to select
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
            <Button variant="outline" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              loading={saving}
              disabled={saving}
              iconName="Save"
              iconPosition="left"
            >
              Save Team Member
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Team Management</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Manage team members displayed on About page
          </p>
        </div>
        <Button onClick={handleCreate} iconName="Plus" iconPosition="left">
          Add Team Member
        </Button>
      </div>

      {error && (
        <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      )}

      {!loading && teamMembers?.length === 0 && (
        <div className="text-center py-12 bg-card border border-border rounded-lg">
          <Icon name="Users" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">No team members yet. Add your first one!</p>
        </div>
      )}

      {!loading && teamMembers?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {teamMembers?.map((member) => (
            <div
              key={member?.id}
              className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-smooth"
            >
              <div className="text-center mb-4">
                {member?.image_url ? (
                  <img
                    src={member?.image_url}
                    alt={member?.name}
                    className="w-24 h-24 rounded-full mx-auto mb-4 object-cover"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full mx-auto mb-4 bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                    <span className="text-white font-semibold text-2xl">
                      {member?.name?.split(' ')?.map(n => n?.[0])?.join('')}
                    </span>
                  </div>
                )}
                <h3 className="text-lg font-semibold text-foreground">{member?.name}</h3>
                <p className="text-sm text-muted-foreground">{member?.role}</p>
                {member?.bio && (
                  <p className="text-xs text-muted-foreground mt-2">{member?.bio}</p>
                )}
                <div className="flex items-center justify-center gap-2 mt-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    member?.is_active ? 'bg-success text-white' : 'bg-muted text-muted-foreground'
                  }`}>
                    {member?.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Order: {member?.display_order}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-center gap-2 pt-4 border-t border-border">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEdit(member)}
                  iconName="Edit"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleToggleActive(member?.id, member?.is_active)}
                  iconName={member?.is_active ? 'EyeOff' : 'Eye'}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDelete(member?.id)}
                  iconName="Trash2"
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TeamManagement;